import React, { useState, useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import { checkoutPaymentContext, trackPaymentOptionsViewed, trackPaymentMethodSelected } from '../../helpers/tracking'
import { PaymentSection } from './styles'
import { useBooleanState, mq } from '../../helpers'
import { gsColors } from '../../styles/Variables'
import { Header2, Text } from '../../styles/Components'
import PaymentMethodModal from './PaymentMethodModal'
import DeleteCardModal from './DeleteCardModal'
import NewPaymentMethod from './NewPaymentMethod'
import ChoosePaymentDropdown from './ChoosePaymentDropdown'
import InlinePaymentSelector from './InlinePaymentSelector'
import PaymentsAccountView from './PaymentsAccountView'
import { updateUser } from '../../services/GraphQlClientService'
import { useUserContext } from '../../components/UserContext'

// Shared left-aligned "Payment method" header + reassurance subtext shown above every
// booking payment state (dropdown, wallet toggle, and plain inline form) per the Cro
// Sprint Figma. Left-aligned inside the centered payment column.
const PaymentHeader = styled.div`
  text-align: left;
  width: 100%;
  margin-bottom: 8px;

  h2 {
    font-family: Euclid;
    font-weight: 600;

    ${mq('max', '768px')} {
      font-size: 22px;
    }
  }

  .payment-subtext {
    color: ${gsColors.meredithGrey};
    margin-top: 8px;
    /* Pin the size: the Text component multiplies fontSize by 0.75 under 768px, which
       would shrink the subtext to ~10.5px on mobile. */
    font-size: 14px !important;
  }
`

/**
 *
 *
 * @param {*} {selectedPaymentMethodFn, createdPaymentMethodFn, isDesktop, selectedPaymentMethodId, paymentMethods}
 * @return {*}
 */

const PaymentCard = ({
  selectedPaymentMethodFn,
  createdPaymentMethodFn,
  isDesktop,
  selectedPaymentMethodId,
  paymentMethods,
  error,
  account,
  me,
  platformPayAvailability,
  noPaymentNeeded,
  paymentOptionsReady = false,
  market,
}) => {
  const setUser = useUserContext().setUser
  const [hasExistingPaymentMethods, setHasExistingPaymentMethods] = useState(
    me?.paymentMethods && me?.paymentMethods.length ? true : false
  )
  const [isPaymentMethodMenuVisible, toggleIsPaymentMethodMenuVisible] =
    useBooleanState()
  const [showAddPaymentMethod, toggleShowAddPaymentMethod] = useBooleanState()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [cardToDestroy, setCardToDestroy] = useState(null)

  if (!hasExistingPaymentMethods) {
    if (paymentMethods && paymentMethods.length) {
      setHasExistingPaymentMethods(true)
    }
  }

  // A user with saved cards gets the dropdown. A card-less user with a device wallet gets
  // the new InlinePaymentSelector toggle (Apple/Google Pay + Credit card); with no wallet
  // they get the plain inline add-card form. walletAvailable is only ever true in the
  // booking flow (the account page never surfaces platform pay), so account-page behavior
  // below is unchanged.
  const walletAvailable = !!(
    platformPayAvailability?.applePay || platformPayAvailability?.googlePay
  )
  const showDropdown = !account && hasExistingPaymentMethods
  const showInlineSelector = !account && !hasExistingPaymentMethods && walletAvailable

  const viewedOptions = useRef(new Set())
  const walletType = platformPayAvailability?.applePay ? 'applePay'
    : platformPayAvailability?.googlePay ? 'googlePay' : null
  const uiType = showDropdown ? 'dropdown' : showInlineSelector ? 'inline_selector' : 'card_form'
  useEffect(() => {
    if (account || noPaymentNeeded || !paymentOptionsReady) {
      return
    }
    const key = JSON.stringify([uiType, walletType, market])
    if (viewedOptions.current.has(key)) {
      return
    }
    viewedOptions.current.add(key)
    trackPaymentOptionsViewed({ uiType, walletType, hasSavedCards: hasExistingPaymentMethods, market })
  }, [account, noPaymentNeeded, paymentOptionsReady, uiType, walletType, hasExistingPaymentMethods, market])

  /** Tracks a choice at its action point; null here means choosing the inline card form. */
  const selectPaymentMethod = (methodId, source = 'user') => {
    if (!account && (source === 'default' || methodId !== selectedPaymentMethodId)) {
      const context = checkoutPaymentContext(methodId, platformPayAvailability)
      trackPaymentMethodSelected({
        ...context,
        paymentMethod: methodId ? context.paymentMethod : 'card',
        previousPaymentMethod: checkoutPaymentContext(selectedPaymentMethodId, platformPayAvailability).paymentMethod,
        source, uiType, market,
      })
    }
    selectedPaymentMethodFn(methodId)
  }

  const selectedPaymentMethod = paymentMethods
    ? paymentMethods.find(({ id }) => id === selectedPaymentMethodId)
    : []

  const togglePaymentSelected = methodId => {
    selectPaymentMethod(methodId)
    toggleIsPaymentMethodMenuVisible()
  }

  const openDestroy = id => {
    setCardToDestroy(id)
    setDeleteModalOpen(true)
  }

  const setDefaultPayment = async id => {
    try {
      await updateUser({ id: me.id, defaultPaymentMethodId: id })
      setUser({
        ...me,
        defaultPaymentMethodId: id
      })
      let storage = JSON.parse(sessionStorage.getItem('book'))
      if (storage) {
        sessionStorage.setItem(
          'book',
          JSON.stringify({
            ...storage,
            selectedPaymentMethodId: id
          })
        )
      }
    } catch (e) {
      console.error(e)
    }
  }

  // A fully credit-covered ($0) appointment with no saved card needs no payment method, so the
  // card input is hidden entirely (the caller also marks the step valid without a selection).
  // A booking error still needs a home, so surface it here even when the input is hidden;
  // otherwise render nothing.
  if (noPaymentNeeded) {
    return error ? (
      <PaymentSection account={account} error>
        <p className="payment-error" role="alert">{error}</p>
      </PaymentSection>
    ) : null
  }

  return (
    <PaymentSection account={account} error={!!error}>
      {!account && (
        <PaymentHeader>
          <Header2 fontSize={22} lineHeight="120%" align="left">
            Payment method
          </Header2>
          <Text className="payment-subtext">
            You won&apos;t be charged until after your appointment.
          </Text>
        </PaymentHeader>
      )}
      {showDropdown ? (
        <ChoosePaymentDropdown
          isPaymentMethodMenuVisible={isPaymentMethodMenuVisible}
          toggleIsPaymentMethodMenuVisible={toggleIsPaymentMethodMenuVisible}
          selectedPaymentMethod={selectedPaymentMethod}
          selectedPaymentMethodId={selectedPaymentMethodId}
          paymentMethods={paymentMethods}
          togglePaymentSelected={togglePaymentSelected}
          toggleShowAddPaymentMethod={toggleShowAddPaymentMethod}
          error={error}
          platformPayAvailability={platformPayAvailability}
        />
      ) : showInlineSelector ? (
        <InlinePaymentSelector
          platformPayAvailability={platformPayAvailability}
          selectedPaymentMethodId={selectedPaymentMethodId}
          selectPaymentMethod={selectPaymentMethod}
          createdPaymentMethodFn={createdPaymentMethodFn}
          paymentMethods={paymentMethods}
          me={me}
          error={error}
        />
      ) : account && hasExistingPaymentMethods ? (
        <PaymentsAccountView
          me={me}
          openDestroy={openDestroy}
          setDefaultPayment={setDefaultPayment}
          toggleShowAddPaymentMethod={toggleShowAddPaymentMethod}
          toggleIsPaymentMethodMenuVisible={toggleIsPaymentMethodMenuVisible}
        />
      ) : (
        <NewPaymentMethod
          data-testid="new-payment-method"
          id="new-payment-method"
          showHeading={!!account}
          {...{
            createdPaymentMethodFn,
            paymentMethods,
            me
          }}
        />
      )}
      <PaymentMethodModal
        data-testid="payment-method-modal"
        isOpen={showAddPaymentMethod}
        close={toggleShowAddPaymentMethod}
        {...{
          createdPaymentMethodFn,
          isDesktop,
          me,
          paymentMethods
        }}
      />
      {cardToDestroy && (
        <DeleteCardModal
          isOpen={deleteModalOpen}
          closeModal={() => {
            setDeleteModalOpen(false)
            setCardToDestroy(null)
          }}
          isDesktop={isDesktop}
          cardToDestroy={cardToDestroy}
          paymentMethods={paymentMethods}
          me={me}
          setUser={setUser}
          selectedPaymentMethodId={selectedPaymentMethodId}
          setHasExistingPaymentMethods={setHasExistingPaymentMethods}
        />
      )}
    </PaymentSection>
  )
}

export default PaymentCard
